<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <BRow
      class="py-2"
    >
      <BCol
        cols="12"
        md="6"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterSearch"
            v-model="tableActivities.filter.search"
            placeholder="search here"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="3"
      >
        <BFormGroup
          label="Role"
          label-for="filterRole"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="tableActivities.selected.role"
            placeholder="filter role here"
            input-id="filterRole"
            label="name"
            clearable
            append-to-body
            :options="(tableActivities.options.roles)"
            :loading="(tableActivities.fetching.roles)"
            :disabled="(tableActivities.busy || tableActivities.fetching.roles)"
            :calculate-position="calculateDropPosition"
          >
            <template #option="{ name }">
              <div
                class="text-break text-wrap"
                v-text="name"
              />
            </template>
            <template #selected-option="{ name }">
              <div
                class="w-225px w-md-100px w-lg-200px text-truncate"
                v-text="name"
              />
            </template>
            <template #no-options="">
              no available role
            </template>
          </VSelect>
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="3"
      >
        <BFormGroup
          label="Activity Type"
          label-for="filterActivityType"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormSelect
            id="filterActivityType"
            v-model="tableActivities.filter.activity_type"
            :options="tableActivities.options.types"
          >
            <template #first>
              <BFormSelectOption
                value=""
                disabled
                v-text="' - - filter here - - '"
              />
            </template>
          </BFormSelect>
        </BFormGroup>
      </BCol>
      <BCol
        cols="12"
      >
        <BButton
          size="sm"
          variant="primary"
          @click="createActivity"
        >
          Create Activity
        </BButton>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableActivities"
      :provider="tableProvider"
      :fields="tableActivities.fields"
      :filter="tableActivities.filter"
      :filter-on="tableActivities.filterOn"
      :sort-by="tableActivities.sortBy"
      :sort-desc="tableActivities.sortDesc"
      :sort-direction="tableActivities.sortDirection"
      :total-rows="tableActivities.totalRows"
      :current-page="tableActivities.currentPage"
      :per-page="tableActivities.perPage"
      :stacked="tableActivities.stacked"
      :busy="tableActivities.busy"
    >
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center">
          <BButton
            size="sm"
            variant="primary"
            :disabled="tableActivities.busy"
            @click.prevent="editActivity(item)"
          >
            Edit
          </BButton>
        </div>
      </template>
    </CustomTable>

    <BModal
      ref="modalActivity"
      size="xl"
      scrollable
      centered
      hide-header
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      content-class="scrollable-tabs"
      modal-class="full-height modal-no-padding"
      aria-hidden="false"
      role="dialog"
      :title="MActivityTitle"
      @shown="() => attachArrowScroller('.nav-activities-tabs')"
      @hidden="() => detachArrowScroller('.nav-activities-tabs')"
    >
      <BTabs
        active-nav-item-class="font-weight-bold text-primary"
        nav-class="scrollable-x-tabs no-scrollbar nav-activities-tabs gap-x-1"
        content-class="scrollable"
        pills
      >
        <template #tabs-start>
          <div class="nav-item">
            <div
              class="d-flex align-items-center position-absolute"
              style="padding: 2px 1px; height: 38px;"
            >
              <i
                class="fas fa-chevron-left bg-primary-light p-1 rounded-lg"
                style="font-size: 20px;"
              />
            </div>
          </div>
        </template>
        <template #tabs-end>
          <div class="nav-item">
            <div
              class="d-flex align-items-center position-absolute"
              style="right: 20px; padding: 2px 1px; height: 38px;"
            >
              <i
                class="fas fa-chevron-right bg-primary-light p-1 rounded-lg"
                style="font-size: 20px;"
              />
            </div>
          </div>
        </template>
        <BTab
          class="p-2 min-h-400px"
        >
          <template #title>
            <div class="text-md-nowrap">
              <i
                :class="['fa-xl fad fa-file-lines', {
                  'text-danger fat fa-shake': vTabActivityDetails()
                }]"
              />
              <b
                :class="['is-tab-active ml-1', {
                  'text-danger': vTabActivityDetails()
                }]"
              >
                Details
              </b>
            </div>
          </template>

          <ValidationObserver
            ref="formActivity"
          >
            <form
              role="form"
              novalidate
              @submit.prevent
            >
              <BRow>

                <BCol
                  cols="12"
                  md="4"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="role_id"
                    name="role"
                    rules="required"
                  >
                    <BFormGroup
                      label="Role"
                      label-for="fRole"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <VSelect
                        v-model="selected.role"
                        input-id="fRole"
                        placeholder="select role here"
                        label="name"
                        clearable
                        append-to-body
                        :options="(options.roles)"
                        :loading="(fetching.roles)"
                        :disabled="(state.busy)"
                        :calculate-position="calculateDropPosition"
                        :class="{ 'is-invalid': errors.length > 0 }"
                      >
                        <template #option="{ name }">
                          <div
                            class="text-break text-wrap"
                            v-text="name"
                          />
                        </template>
                        <template #selected-option="{ name }">
                          <div
                            class="w-120px w-md-200px text-truncate"
                            v-text="name"
                          />
                        </template>
                        <template #no-options="">
                          no available roles
                        </template>
                      </VSelect>
                      <div
                        v-if="errors.length > 0"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol
                  cols="12"
                  md="4"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="activity_type"
                    name="activity type"
                    rules="required"
                  >
                    <BFormGroup
                      label="Activity Type"
                      label-for="fActivityType"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <VSelect
                        v-model="selected.type"
                        input-id="fActivityType"
                        placeholder="select activity type here"
                        label="name"
                        clearable
                        append-to-body
                        :options="(options.types)"
                        :loading="(fetching.types)"
                        :disabled="(state.busy)"
                        :calculate-position="calculateDropPosition"
                        :class="{ 'is-invalid': errors.length > 0 }"
                      >
                        <template #option="{ name }">
                          <div
                            class="text-break text-wrap"
                            v-text="name"
                          />
                        </template>
                        <template #selected-option="{ name }">
                          <div
                            class="w-120px w-md-200px text-truncate"
                            v-text="name"
                          />
                        </template>
                        <template #no-options="">
                          no available activity types
                        </template>
                      </VSelect>
                      <div
                        v-if="errors.length > 0"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol cols="12" />

                <BCol
                  cols="12"
                  md="4"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    name="activity code"
                    vid="activity_code"
                    rules="max:30"
                  >
                    <BFormGroup
                      label="Activity Code"
                      label-for="fActivityCode"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <BFormInput
                        id="fActivityCode"
                        v-model="formActivity.activity_code"
                        type="text"
                        maxlength="30"
                        autocomplete="off"
                        placeholder="enter code here"
                        :state="errors.length > 0 ? false : null"
                      />
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol
                  cols="12"
                  md="8"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    name="activity name"
                    vid="activity_name"
                    rules="required|max:100"
                  >
                    <BFormGroup
                      label="Activity Name"
                      label-for="fActivityName"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <BFormInput
                        id="fActivityName"
                        v-model="formActivity.activity_name"
                        type="text"
                        maxlength="100"
                        autocomplete="off"
                        placeholder="enter activity name here"
                        :state="errors.length > 0 ? false : null"
                      />
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol
                  cols="12"
                  md="8"
                >
                  <BRow>
                    <BCol
                      cols="12"
                      md="6"
                    >
                      <BFormGroup>
                        <BFormCheckbox
                          id="fSaveAsInteraction"
                          v-model="formActivity.rules.interaction"
                          switch
                          value="1"
                          name="save_as_interaction"
                          unchecked-value="0"
                          :disabled="state.busy"
                        >
                          Save as Interaction?
                        </BFormCheckbox>
                      </BFormGroup>
                    </BCol>

                    <BCol
                      cols="12"
                      md="6"
                    >
                      <BFormGroup>
                        <BFormCheckbox
                          id="fCustomer"
                          v-model="formActivity.rules.customer"
                          switch
                          value="1"
                          name="customer"
                          unchecked-value="0"
                          :disabled="state.busy"
                        >
                          Can use Customer?
                        </BFormCheckbox>
                      </BFormGroup>
                    </BCol>

                    <BCol
                      cols="12"
                      md="6"
                    >
                      <BFormGroup>
                        <BFormCheckbox
                          id="fNeedsValidation"
                          v-model="formActivity.rules.needs_validation"
                          switch
                          value="1"
                          name="needs_validation"
                          unchecked-value="0"
                          :disabled="state.busy"
                        >
                          Needs Validation?
                        </BFormCheckbox>
                      </BFormGroup>
                    </BCol>
                  </BRow>
                </BCol>

                <BCol
                  cols="12"
                >
                  <BRow>
                    <BCol
                      cols="12"
                    >
                      <BFormGroup>
                        <BFormCheckbox
                          id="fSignedOnly"
                          v-model="formActivity.rules.signed_customers_only"
                          switch
                          value="1"
                          name="signed_customers_only"
                          unchecked-value="0"
                          :disabled="state.busy || Number(formActivity.rules.customer) === 0"
                        >
                          Signed Customers Only?
                        </BFormCheckbox>
                      </BFormGroup>
                    </BCol>

                    <BCol
                      cols="12"
                      md="4"
                    >
                      <ValidationProvider
                        v-slot="{ errors }"
                        vid="activity_id"
                        name="activity"
                      >
                        <BFormGroup
                          label="Activity Pre-requisite"
                          label-for="fActivity"
                          label-class="font-weight-bolder"
                          class="mb-2"
                        >
                          <VSelect
                            v-model="selected.activity"
                            input-id="fActivity"
                            label="activity_name"
                            append-to-body
                            placeholder="select activity here"
                            :options="options.activities"
                            :disabled="fetching.activities || state.busy || Number(formActivity.rules.signed_customers_only) === 0"
                            :loading="fetching.activities"
                            :class="{ 'is-invalid': errors.length > 0 }"
                            @search="searchActivities"
                          >
                            <template #option="{ activity_name }">
                              <div
                                class="text-break text-wrap"
                                v-text="activity_name"
                              />
                            </template>
                            <template #selected-option="{ activity_name }">
                              <div
                                class="w-120px w-md-200px text-truncate"
                                v-text="activity_name"
                              />
                            </template>
                            <template #no-options="">
                              no available activity
                            </template>
                          </VSelect>
                          <div
                            v-if="(errors.length > 0)"
                            class="invalid-feedback"
                            v-text="errors[0]"
                          />
                        </BFormGroup>
                      </ValidationProvider>
                    </BCol>

                    <BCol
                      cols="12"
                      md="3"
                    >
                      <ValidationProvider
                        v-slot="{ errors }"
                        name="start date"
                        vid="start_date"
                        :rules="{
                          required: Number(formActivity.rules.signed_customers_only) === 1
                        }"
                      >
                        <BFormGroup
                          label="Start Date"
                          label-for="fStartDate"
                          label-class="font-weight-bolder"
                          class="mb-2"
                        >
                          <BFormInput
                            id="fStartDate"
                            v-model="formActivity.rules.start_date"
                            type="date"
                            autocomplete="off"
                            :state="errors.length > 0 ? false : null"
                            :disabled="Number(formActivity.rules.signed_customers_only) === 0"
                          />
                          <div
                            v-if="(errors.length > 0)"
                            class="invalid-feedback"
                            v-text="errors[0]"
                          />
                        </BFormGroup>
                      </ValidationProvider>
                    </BCol>

                    <BCol
                      cols="12"
                      md="3"
                    >
                      <ValidationProvider
                        v-slot="{ errors }"
                        name="end date"
                        vid="end_date"
                        :rules="{
                          required: Number(formActivity.rules.signed_customers_only) === 1
                        }"
                      >
                        <BFormGroup
                          label="End Date"
                          label-for="fEndDate"
                          label-class="font-weight-bolder"
                          class="mb-2"
                        >
                          <BFormInput
                            id="fEndDate"
                            v-model="formActivity.rules.end_date"
                            type="date"
                            autocomplete="off"
                            :state="errors.length > 0 ? false : null"
                            :disabled="Number(formActivity.rules.signed_customers_only) === 0"
                          />
                          <div
                            v-if="(errors.length > 0)"
                            class="invalid-feedback"
                            v-text="errors[0]"
                          />
                        </BFormGroup>
                      </ValidationProvider>
                    </BCol>
                  </BRow>
                </BCol>

                <BCol
                  v-if="state.editing"
                  cols="12"
                >
                  <BFormGroup>
                    <BFormCheckbox
                      id="fActive"
                      v-model="formActivity.active"
                      switch
                      value="1"
                      name="active"
                      unchecked-value="0"
                      :disabled="state.busy"
                    >
                      Is Active?
                    </BFormCheckbox>
                  </BFormGroup>
                </BCol>
              </BRow>

            </form>
          </ValidationObserver>
        </BTab>
        <BTab
          class="p-2 min-h-400px"
        >
          <template #title>
            <div class="text-md-nowrap">
              <i
                :class="['fa-xl fad fa-map-location-dot', {
                  'text-danger fat fa-shake': vTabDocumentTypes()
                }]"
              />
              <b
                :class="['is-tab-active ml-1', {
                  'text-danger': vTabDocumentTypes()
                }]"
              >
                Document Types
              </b>
            </div>
          </template>

          <DocumentTypes
            ref="tableDocumentTypes"
            :activity="formActivity"
            :document-types="formActivity.documents"
            @updated="updateActivityDocumentTypes"
          />
        </BTab>
      </BTabs>

      <template #modal-footer="{ cancel }">
        <BButton
          variant="primary"
          :disabled="state.busy"
          @click.prevent="validateActivity"
        >
          {{ state.editing ? 'Update' : 'Create' }} Activity
        </BButton>
        <BButton

          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Discard
        </BButton>
      </template>

    </BModal>
  </BContainer>
</template>

<script>
import { mapGetters } from 'vuex'
import QUERY from 'lodash'

import DocumentTypes from '@/views/admin/maintenances/activities/DocumentTypes.vue'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MRoles from '@/mixins/accounts/roles'
import MActivities from '@/mixins/maintenances/activities'

import { SAdminActivity } from '@/services/admin'

export default {
  name: 'VAdminActivities',

  components: {
    DocumentTypes
  },

  mixins: [
    MxMisc,
    MActivities,
    MxFormatter,
    MRoles
  ],

  middleware: [
    'auth',
    'admin'
  ],

  metaInfo: () => ({
    title: 'Activities'
  }),

  data () {
    return {
      state: {
        busy: false,
        editing: false
      },
      fetching: {
        types: false
      },
      selected: {
        type: ''
      },
      options: {
        types: [
          { id: 'Interaction', name: 'Interaction' },
          { id: 'Event', name: 'Event' },
          { id: 'Others', name: 'Others' }
        ]
      },
      formActivity: {
        id: '',
        role_id: '',
        activity_type: '',
        activity_code: '',
        activity_name: '',
        activity_id: '',
        documents: [],
        rules: {
          customer: 1,
          interaction: 0,
          needs_validation: 0,
          signed_customers_only: 0,
          start_date: '',
          end_date: ''
        },
        active: 1
      },
      tableActivities: {
        busy: false,
        fetching: {
          roles: false
        },
        options: {
          roles: [],
          types: [
            'All',
            'Interaction',
            'Event',
            'Others'
          ]
        },
        selected: {
          role: ''
        },
        filter: {
          search: '',
          role: '',
          activity_type: 'All'
        },
        filterOn: [],
        stacked: 'sm',
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 12, key: 'action', class: 'text-center w-md-125px' },
          { mobile: 6, key: 'created_at', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true },
          { mobile: 1, key: 'role.role_name', label: 'Role', class: 'min-w-175px' },
          { mobile: 2, key: 'activity_type', class: 'min-w-150px' },
          { mobile: 3, key: 'activity_code', class: 'min-w-150px' },
          { mobile: 4, key: 'activity_name', class: 'min-w-175px' },
          { mobile: 10, key: 'active', class: 'text-right w-md-75px', formatter: this.yesOrNo },
          { mobile: 11, key: 'updated_at', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: {
    ...mapGetters({
      currentUser: 'auth/user'
    }),

    MActivityTitle () {
      return this.state.editing ? 'Edit Activity' : 'Create Activity'
    }
  },

  watch: {
    'selected.role' (role) {
      this.formActivity.role_id = role?.id || ''

      if (role?.id && Number(this.formActivity.rules.signed_customers_only) === 1) {
        this.getActivities(this).finally(() => {
          if (this.editable.activity) {
            this.selected.activity = QUERY.find(this.options.activities, {
              id: this.editable.activity
            }) || ''

            this.editable.activity = ''
          }
        })
      } else {
        this.selected.activity = ''
        this.options.activities = []
      }
    },

    'selected.type' (type) {
      this.formActivity.activity_type = type?.id || ''
      this.selected.activityType = type?.id || ''

      if (type?.id && Number(this.formActivity.rules.signed_customers_only) === 1) {
        this.getActivities(this).finally(() => {
          if (this.editable.activity) {
            this.selected.activity = QUERY.find(this.options.activities, {
              id: this.editable.activity
            }) || ''

            this.editable.activity = ''
          }
        })
      } else {
        this.selected.activity = ''
        this.options.activities = []
      }
    },

    'selected.activity' (activity) {
      this.formActivity.activity_id = activity?.id || ''
    },

    'formActivity.rules.signed_customers_only' (signedCustomerOnly) {
      this.formActivity.rules.start_date = ''
      this.formActivity.rules.end_date = ''

      if (Number(signedCustomerOnly) === 1) {
        this.getActivities(this).finally(() => {
          if (this.editable.activity) {
            this.selected.activity = QUERY.find(this.options.activities, {
              id: this.editable.activity
            }) || ''

            this.editable.activity = ''
          }
        })
      } else {
        this.selected.activity = ''
        this.options.activities = []
      }
    },

    'tableActivities.selected.role' (role) {
      this.tableActivities.filter.role = role?.id || ''
    }
  },

  mounted () {
    if (this.currentUser) {
      this.getRoles(this.tableActivities)
    }
  },

  methods: {

    vTabActivityDetails () {
      if (this.$refs.formActivity) {
        const formActivityErrors = this.$refs.formActivity.errors
        return Object.keys(formActivityErrors).some(
          errorKey => formActivityErrors[errorKey]?.length > 0
        )
      }

      return false
    },

    vTabDocumentTypes () {
      if (this.$refs.tableDocumentTypes) {
        const formDocument = this.$refs.tableDocumentTypes.$refs.formDocument
        if (formDocument && formDocument?.errors) {
          return Object.keys(formDocument.errors).some(
            errorKey => formDocument.errors[errorKey]?.length > 0
          )
        }
      }

      return false
    },

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableActivities.busy = true

      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        role_id: filter.role,
        activity_type: filter.activity_type
      }

      return await SAdminActivity.get(payload).then(
        ({ data }) => {
          this.tableActivities.totalRows = data.total_rows
          return data.items
        }
      ).catch(() => {
        this.tableActivities.totalRows = 0
        return []
      }).finally(() => {
        this.tableActivities.busy = false
      })
    },

    updateActivityDocumentTypes (documents) {
      this.formActivity.documents = documents
    },

    createActivity () {
      this.state.editing = false
      this.selected.type = null

      this.formActivity = Object.assign(this.formActivity, {
        id: '',
        role_id: '',
        activity_type: '',
        activity_code: '',
        activity_name: '',
        documents: [],
        rules: {
          customer: 1,
          interaction: 0,
          needs_validation: 0,
          signed_customers_only: 0,
          start_date: '',
          end_date: ''
        },
        active: 1
      })

      this.getRoles(this)

      this.$refs.modalActivity.show()
    },

    editActivity (activity) {
      this.state.editing = true

      this.editable.activity = activity?.activity_id

      this.formActivity = Object.assign(this.formActivity, {
        id: activity.id,
        activity_type: activity.activity_type,
        activity_code: activity.activity_code,
        activity_name: activity.activity_name,
        event_plan: activity.event_plan,
        documents: activity.documents,
        rules: {
          customer: activity.rules?.customer || 1,
          interaction: activity.rules?.interaction || 1,
          needs_validation: activity.rules?.needs_validation || 0,
          signed_customers_only: activity.rules?.signed_customers_only || 0,
          start_date: activity.rules?.start_date || '',
          end_date: activity.rules?.end_date || ''
        },
        active: activity.active
      })

      this.selected.type = QUERY.find(this.options.types, {
        id: activity.activity_type
      })

      this.getRoles(this).finally(() => {
        this.selected.role = QUERY.find(this.options.roles, {
          id: activity?.role_id || ''
        }) || ''
      })

      this.$refs.modalActivity.show()
    },

    async validateActivity () {
      await this.$refs.formActivity.validate().then(
        async validActivity => {
          if (!validActivity) {
            return this.swalInvalid()
          }

          this.swalConfirm({
            html: this.state.editing ? '<h6>Update this Activity?</h6>' : '<h6>Create this Activity?</h6>',
            confirmButtonText: this.state.editing ? 'Update' : 'Create',
            preConfirm: async () => {
              this.state.busy = true

              const createOrUpdate = async () => {
                return this.state.editing ? this.putActivity() : this.postActivity()
              }

              return createOrUpdate().catch(
                ({ message }) => {
                  if (message && message?.errors) {
                    this.$refs.formActivity.setErrors(message.errors)

                    if (message.errors?.documents) {
                      this.$refs.tableDocumentTypes.setErrors({
                        document_type_id: message.errors.documents
                      })
                    }
                  }
                }
              ).finally(() => {
                this.state.busy = false
              })
            }
          })
        }
      )
    },

    async postActivity () {
      return await SAdminActivity.post(this.formActivity).then(
        ({ data: { message } }) => {
          this.swalSuccess(message).then(() => {
            this.$refs.modalActivity.hide()
            this.$refs.tableActivities.refetch()
          })
        }
      )
    },

    async putActivity () {
      return await SAdminActivity.put(this.formActivity).then(
        ({ data: { message, activity } }) => {
          this.swalSuccess(message).then(() => {
            this.$refs.modalActivity.hide()
            this.$refs.tableActivities.updateRow({
              id: activity.id,
              role_id: activity?.role_id || '',
              role: activity?.role || {},
              activity_type: activity.activity_type,
              activity_code: activity.activity_code,
              activity_name: activity.activity_name,
              event_plan: activity.event_plan,
              documents: activity.documents,
              rules: {
                customer: activity.rules?.customer || 1,
                interaction: activity.rules?.interaction || 1,
                needs_validation: activity.rules?.needs_validation || 0,
                signed_customers_only: activity.rules?.signed_customers_only || 0,
                start_date: activity.rules?.start_date || '',
                end_date: activity.rules?.end_date || ''
              },
              active: activity.active,
              updated_at: activity.updated_at
            })
          })
        }
      )
    }
  }
}
</script>
